import { useCallback, useEffect, useRef, useState } from 'react';

import './SequencerConnect.css';
import { SequencerConnectRunDetail } from './SequencerConnectRunDetail';
import { sqApi, sqErrorMessage, type SqRun, type SqRunStatus } from './sequencerApi';

/** The wizard is six steps; the run row carries the pointer as current_step. */
const TOTAL_STEPS = 6;

const NAME_INPUT_ID = 'sq-run-name';
const NAME_HINT_ID = 'sq-run-name-hint';

const TOAST_MS = 6000;

/**
 * Run status to badge variant, in the colour language the whole feature shares:
 * sage for finished, blue for in flight (the app-wide running colour -
 * .trf-status--running, .srm-status--running and .fd-badge--running are all
 * blue), honey for "needs watching", rose for failed, muted for not-yet-started.
 *
 * `qa` is honey rather than blue on purpose: work IS happening, but it is the
 * verification pass, and the operator must not read it as "still connecting".
 */
const STATUS_BADGE: Record<SqRunStatus, string> = {
  draft: 'muted',
  in_progress: 'info',
  running: 'running',
  qa: 'warn',
  completed: 'ok',
  failed: 'err',
};

const STATUS_LABEL: Record<SqRunStatus, string> = {
  draft: 'draft',
  in_progress: 'in progress',
  running: 'running',
  qa: 'QA check',
  completed: 'completed',
  failed: 'failed',
};

interface Toast {
  type: 'success' | 'error' | 'info' | 'warn';
  message: string;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Archive is ORTHOGONAL to status - archived_at can be set on a run in any
 * status - so it is read first and wins the badge. An archived run is closed for
 * good whatever it was doing when it was closed, and showing it as `running`
 * would invite the operator to wait for something that will never move again.
 */
function badgeFor(run: SqRun): { variant: string; label: string } {
  if (run.archived_at) return { variant: 'muted', label: 'archived' };
  return { variant: STATUS_BADGE[run.status], label: STATUS_LABEL[run.status] };
}

/**
 * Before Start the interesting number is where the wizard got to; after it, how
 * many mailboxes have landed. `already` counts towards the numerator because an
 * account PlusVibe already had is a finished mailbox, not an outstanding one.
 */
function progressFor(run: SqRun): string {
  if (run.status === 'draft' || run.status === 'in_progress') {
    // A connect-only run skips the workspace and parameters steps.
    return run.sequencer === 'warmuptool'
      ? `step ${Math.min(run.current_step, 4)} of 4`
      : `step ${run.current_step} of ${TOTAL_STEPS}`;
  }
  return `${run.accounts_done + run.accounts_already}/${run.accounts_total}`;
}

export function SequencerConnect(): JSX.Element {
  const [runs, setRuns] = useState<SqRun[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  const [archiveTarget, setArchiveTarget] = useState<SqRun | null>(null);
  const [archiving, setArchiving] = useState(false);

  /**
   * One toast at a time, one timer at a time - the pattern the foreign-dns
   * screens already use. The previous timer has to be CANCELLED, not outlived: a
   * toast raised near the end of an earlier toast's window is otherwise wiped by
   * that earlier timer firing. Cleared on unmount too, so a queued callback
   * cannot fire into a dead component.
   */
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((type: Toast['type'], message: string) => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    setToast({ type, message });
    toastTimer.current = window.setTimeout(() => {
      toastTimer.current = null;
      setToast(null);
    }, TOAST_MS);
  }, []);

  useEffect(
    () => () => {
      if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  /**
   * A failed load is NOT an empty list, and is not a toast either. "No runs yet."
   * reads as "there is nothing here", which invites a duplicate run for a sheet
   * that already has one - and a 6s toast saying otherwise would be gone (or
   * overwritten by the next success message) long before the operator acts on it.
   * It is state, it persists until a load succeeds, and `runs` keeps its last
   * good contents so a failed refresh does not blank a list that was on screen.
   */
  const refreshRuns = useCallback(async (): Promise<void> => {
    try {
      const { runs: fresh } = await sqApi.listRuns();
      setRuns(fresh);
      setListError(null);
    } catch (err) {
      setListError(sqErrorMessage(err));
    } finally {
      setLoaded(true);
    }
  }, []);

  // The list does not poll. Nothing on it changes without an operator acting,
  // and the only live state in this feature (a run mid-flight) is polled by the
  // detail view while it is open. The list refetches on mount, after a create or
  // an archive, and when the operator comes back from a run.
  useEffect(() => {
    void refreshRuns();
  }, [refreshRuns]);

  async function createRun(): Promise<void> {
    const trimmed = name.trim();
    if (trimmed === '') return;
    setCreating(true);
    try {
      const run = await sqApi.createRun(trimmed);
      setName('');
      setShowCreate(false);
      await refreshRuns();
      showToast('success', `Run created: ${run.name}`);
    } catch (err) {
      showToast('error', sqErrorMessage(err));
    } finally {
      setCreating(false);
    }
  }

  async function archiveRun(run: SqRun): Promise<void> {
    setArchiving(true);
    try {
      await sqApi.archive(run.id);
      setArchiveTarget(null);
      await refreshRuns();
      showToast('success', `Archived: ${run.name}. Its credentials were purged.`);
    } catch (err) {
      showToast('error', sqErrorMessage(err));
    } finally {
      setArchiving(false);
    }
  }

  // The detail view takes only {runId, onBack} - it loads its own run and owns
  // every write from there. Coming back refreshes the list, because the wizard
  // it was just in is what changed the row.
  if (openRunId) {
    return (
      <SequencerConnectRunDetail
        runId={openRunId}
        onBack={() => {
          setOpenRunId(null);
          void refreshRuns();
        }}
      />
    );
  }

  const active = runs.filter((r) => !r.archived_at);
  const archived = runs.filter((r) => r.archived_at);

  const renderRow = (run: SqRun): JSX.Element => {
    const badge = badgeFor(run);
    return (
      // No row-level onClick. A clickable <tr> is mouse-only unless it grows
      // tabIndex + role + a key handler, and the row already carries a real Open
      // button a keyboard reaches for free.
      <tr key={run.id}>
        <td className="sq-cell-mono">{run.name}</td>
        <td className="sq-cell-faint">{run.sequencer}</td>
        <td className="sq-cell-faint">{fmtDate(run.created_at)}</td>
        <td className="sq-cell-faint">
          {run.created_by}
          {run.created_by_role !== run.created_by ? ` (${run.created_by_role})` : ''}
        </td>
        <td className="sq-col-center">
          <span className={`sq-badge sq-badge--${badge.variant}`}>{badge.label}</span>
        </td>
        <td className="sq-col-center sq-cell-faint">{progressFor(run)}</td>
        <td className="sq-col-center">
          <div className="sq-rowactions">
            <button
              type="button"
              className="sq-btn sq-btn--secondary sq-btn--small"
              onClick={() => setOpenRunId(run.id)}
            >
              Open
            </button>
            {!run.archived_at && (
              <button
                type="button"
                className="sq-btn sq-btn--ghost sq-btn--small"
                onClick={() => setArchiveTarget(run)}
              >
                Archive
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="sq">
      <div className="sq-scroll">
        <header className="sq-header">
          <h1 className="sq-title">Sequencer Connect</h1>
          <p className="sq-sub">
            Connect Google Workspace mailboxes to a sending sequencer (PlusVibe, Instantly, Smartlead, Bison or Warmuptool) and
            apply our standard sending and warmup config. One run per Google Sheet: paste the
            customer's API key and the sheet's account columns, confirm the workspace and the
            parameters, and start. Every account is connected, configured, tagged and then QA
            verified against the live workspace. The borrowed credentials are held only for the
            life of the run.
          </p>
        </header>

        {listError && (
          <div className="sq-toast sq-toast--error">Could not load the run list: {listError}</div>
        )}
        {toast && <div className={`sq-toast sq-toast--${toast.type}`}>{toast.message}</div>}

        <section className="sq-card">
          <div className="sq-card-header">
            <h2 className="sq-card-title">Runs</h2>
            <div className="sq-actions">
              <button
                type="button"
                className="sq-btn sq-btn--ghost sq-btn--small"
                onClick={() => void refreshRuns()}
              >
                Refresh
              </button>
              <button
                type="button"
                className="sq-btn sq-btn--primary"
                onClick={() => setShowCreate(true)}
              >
                Run Sequencer Connect &amp; Config
              </button>
            </div>
          </div>
          <div className="sq-table-wrap">
            <table className="sq-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Sequencer</th>
                  <th>Created</th>
                  <th>By</th>
                  <th className="sq-col-center">Status</th>
                  <th className="sq-col-center">Progress</th>
                  <th className="sq-col-center" />
                </tr>
              </thead>
              <tbody>
                {active.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="sq-empty">
                      {!loaded
                        ? 'Loading runs...'
                        : listError !== null
                          ? 'The run list could not be loaded - this is not an empty list. Use Refresh to try again.'
                          : archived.length > 0
                            ? // Every run there is has been archived. Saying "no
                              // runs yet" above a populated Archived section
                              // reads as a load failure.
                              'No open runs. Every run is archived - see the section below.'
                            : 'No runs yet.'}
                    </td>
                  </tr>
                ) : (
                  active.map(renderRow)
                )}
              </tbody>
            </table>
          </div>
        </section>

        {archived.length > 0 && (
          <details className="sq-archived">
            <summary className="sq-archived-summary">Archived ({archived.length})</summary>
            <div className="sq-table-wrap">
              <table className="sq-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Sequencer</th>
                    <th>Created</th>
                    <th>By</th>
                    <th className="sq-col-center">Status</th>
                    <th className="sq-col-center">Progress</th>
                    <th className="sq-col-center" />
                  </tr>
                </thead>
                <tbody>{archived.map(renderRow)}</tbody>
              </table>
            </div>
          </details>
        )}
      </div>

      {showCreate && (
        <div className="sq-modal-overlay">
          <div className="sq-modal">
            <div className="sq-modal-header">
              <h3 className="sq-modal-title">Run Sequencer Connect &amp; Config</h3>
            </div>
            <div className="sq-modal-body">
              {/* The hint is deliberately OUTSIDE the label and referenced with
                  aria-describedby. Nested inside it, the computed accessible
                  name becomes the label and the whole hint run together. */}
              <div className="sq-field">
                <label className="sq-field-label" htmlFor={NAME_INPUT_ID}>
                  Full Google Sheet name
                </label>
                <input
                  id={NAME_INPUT_ID}
                  aria-describedby={NAME_HINT_ID}
                  className="sq-input sq-mono"
                  type="text"
                  value={name}
                  maxLength={300}
                  placeholder="2025-12-25 - tomas@premiuminboxes.com - recwUQQq4knpUH9oY"
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    // `creating` matters here and not only on the button: the
                    // button is disabled mid-create, but Enter bypasses it, and
                    // two quick presses would create two identical draft runs
                    // that nothing downstream can tell apart (names are
                    // deliberately not unique).
                    if (e.key === 'Enter' && !creating) void createRun();
                  }}
                  spellCheck={false}
                  autoFocus
                />
                <span className="sq-hint" id={NAME_HINT_ID}>
                  Paste the sheet name exactly as it is in Drive - date, customer email and
                  Airtable record id. It is the label for this run and nothing reads the sheet
                  itself.
                </span>
              </div>
            </div>
            <div className="sq-modal-footer">
              <button
                type="button"
                className="sq-btn sq-btn--primary"
                onClick={() => void createRun()}
                disabled={creating || name.trim() === ''}
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                className="sq-btn sq-btn--secondary"
                onClick={() => setShowCreate(false)}
                disabled={creating}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {archiveTarget && (
        <div className="sq-modal-overlay">
          <div className="sq-modal">
            <div className="sq-modal-header">
              <h3 className="sq-modal-title">Archive this run?</h3>
            </div>
            <div className="sq-modal-body">
              <p>
                Archive this run? Credentials are purged and the run closes. The record stays
                visible.
              </p>
              <p className="sq-cell-faint">{archiveTarget.name}</p>
            </div>
            <div className="sq-modal-footer">
              <button
                type="button"
                className="sq-btn sq-btn--danger"
                onClick={() => void archiveRun(archiveTarget)}
                disabled={archiving}
              >
                {archiving ? 'Archiving...' : 'Archive and purge credentials'}
              </button>
              <button
                type="button"
                className="sq-btn sq-btn--secondary"
                onClick={() => setArchiveTarget(null)}
                disabled={archiving}
              >
                Keep the run open
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
